/* ==========================================
   search.js — Búsqueda predictiva
   Debounce 300ms, mínimo 3 caracteres, /search/suggest.json,
   resultados agrupados, navegación teclado (flechas, Enter),
   role="combobox", aria-expanded, aria-activedescendant.
   Dependencias: theme.js (debounce, trapFocus, showOverlay, hideOverlay)
   ========================================== */

(function() {
  var container = document.querySelector('[data-predictive-search]');
  if (!container) return;

  var input = container.querySelector('[data-predictive-input]');
  var resultsContainer = container.querySelector('[data-predictive-results]');
  var viewAllLink = container.querySelector('[data-predictive-view-all]');
  var closeBtn = container.querySelector('[data-predictive-close]');
  var combobox = container.querySelector('[role="combobox"]');
  var stringsEl = document.querySelector('[data-search-strings]');
  var strings = {};
  var releaseFocusFn = null;
  var triggerEl = null;
  var activeIndex = -1;
  var results = [];
  var abortController = null;

  try {
    if (stringsEl) strings = JSON.parse(stringsEl.textContent);
  } catch(e) {}

  /* ---- Open / Close ---- */

  function open() {
    container.hidden = false;
    container.setAttribute('aria-hidden', 'false');
    showOverlay();
    /* Delay trapFocus so the input can receive focus first */
    setTimeout(function() {
      releaseFocusFn = trapFocus(container);
      input.focus();
      input.select();
    }, 50);
  }

  function close() {
    container.hidden = true;
    container.setAttribute('aria-hidden', 'true');
    hideOverlay();
    if (releaseFocusFn) { releaseFocusFn(); releaseFocusFn = null; }
    if (triggerEl) { triggerEl.focus(); triggerEl = null; }
    resultsContainer.innerHTML = '';
    viewAllLink.hidden = true;
    input.value = '';
    activeIndex = -1;
    results = [];
    if (combobox) combobox.setAttribute('aria-expanded', 'false');
    if (abortController) { abortController.abort(); abortController = null; }
  }

  /* Open from header search trigger */
  document.addEventListener('click', function(e) {
    var trigger = e.target.closest('[data-search-toggle]');
    if (!trigger) return;
    e.preventDefault();
    triggerEl = trigger;
    open();
  });

  if (closeBtn) closeBtn.addEventListener('click', close);

  container.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') close();
  });

  container.addEventListener('click', function(e) {
    if (e.target === container) close();
  });

  document.addEventListener('drawer:close', close);

  /* ---- Search with debounce ---- */

  var doSearch = debounce(function() {
    var query = input.value.trim();

    if (query.length < 3) {
      resultsContainer.innerHTML = '';
      viewAllLink.hidden = true;
      if (combobox) combobox.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
      results = [];
      return;
    }

    /* Abort previous request */
    if (abortController) abortController.abort();
    abortController = new AbortController();

    /* Show loading spinner */
    resultsContainer.innerHTML = '<div class="predictive-search__loading"><div class="predictive-search__spinner"></div></div>';

    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
              '&resources[type]=product,collection,page,article&resources[limit]=4';

    fetch(url, { signal: abortController.signal })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        renderResults(data.resources.results, query);
      })
      .catch(function(err) {
        if (err.name !== 'AbortError') {
          resultsContainer.innerHTML = '<p class="predictive-search__no-results">' +
            escapeHtml(strings.no_results || 'No results found') + '</p>';
        }
      });
  }, 300);

  input.addEventListener('input', doSearch);

  /* ---- Render results ---- */

  function renderResults(data, query) {
    var html = '';
    results = [];
    var resultId = 0;

    var groups = [
      { key: 'products', label: strings.products || 'Products' },
      { key: 'collections', label: strings.collections || 'Collections' },
      { key: 'pages', label: strings.pages || 'Pages' },
      { key: 'articles', label: strings.articles || 'Articles' }
    ];

    groups.forEach(function(group) {
      var items = data[group.key] || [];
      if (items.length === 0) return;

      html += '<div class="predictive-search__group-title">' + escapeHtml(group.label) + '</div>';

      items.forEach(function(item) {
        var id = 'predictive-result-' + resultId;
        var itemUrl = item.url;
        var title = item.title;
        var image = '';
        var meta = '';
        var price = '';

        if (group.key === 'products') {
          if (item.image) {
            image = '<img src="' + item.image + '" alt="" class="predictive-search__result-image" loading="lazy" width="44" height="44">';
          }
          price = '<span class="predictive-search__result-price">' + formatMoney(item.price) + '</span>';
          if (item.vendor) meta = item.vendor;
        } else if (group.key === 'articles' && item.image) {
          image = '<img src="' + item.image + '" alt="" class="predictive-search__result-image" loading="lazy" width="44" height="44">';
        }

        html += '<a href="' + itemUrl + '" class="predictive-search__result" id="' + id + '" role="option" aria-selected="false">';
        if (image) html += image;
        html += '<div class="predictive-search__result-info">';
        html += '<div class="predictive-search__result-title">' + escapeHtml(title) + '</div>';
        if (meta) html += '<div class="predictive-search__result-meta">' + escapeHtml(meta) + '</div>';
        html += '</div>';
        if (price) html += price;
        html += '</a>';

        results.push({ id: id, url: itemUrl });
        resultId++;
      });
    });

    if (html === '') {
      html = '<p class="predictive-search__no-results">' +
        escapeHtml(strings.no_results || 'No results found') + '</p>';
      viewAllLink.hidden = true;
    } else {
      viewAllLink.href = '/search?q=' + encodeURIComponent(query);
      viewAllLink.hidden = false;
    }

    resultsContainer.innerHTML = html;
    activeIndex = -1;
    if (combobox) combobox.setAttribute('aria-expanded', results.length > 0 ? 'true' : 'false');
  }

  /* ---- Keyboard navigation ---- */

  input.addEventListener('keydown', function(e) {
    if (results.length === 0) {
      /* Allow Enter to submit to search page */
      if (e.key === 'Enter' && input.value.trim().length >= 3) {
        e.preventDefault();
        window.location.href = '/search?q=' + encodeURIComponent(input.value.trim());
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = Math.min(activeIndex + 1, results.length - 1);
      highlightResult();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = Math.max(activeIndex - 1, -1);
      highlightResult();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0) {
        window.location.href = results[activeIndex].url;
      } else {
        window.location.href = '/search?q=' + encodeURIComponent(input.value.trim());
      }
    }
  });

  function highlightResult() {
    var allResults = resultsContainer.querySelectorAll('.predictive-search__result');
    allResults.forEach(function(el, i) {
      var isActive = i === activeIndex;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (activeIndex >= 0 && results[activeIndex]) {
      input.setAttribute('aria-activedescendant', results[activeIndex].id);
      var activeEl = document.getElementById(results[activeIndex].id);
      if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
    } else {
      input.setAttribute('aria-activedescendant', '');
    }
  }

  /* ---- Helpers ---- */

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return '$' + (parseInt(cents, 10) / 100).toFixed(2);
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

})();
